import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* --------------------- Import config variables --------------------- */
import {
  CHART_COLOR_SET,
  COLOR,
  DEPOSIT_DEFAULT,
  DEPOSIT_DEFAULT_SK,
  INDEXED_RATE_DEFAULT,
  INIT_DEPOSIT_MAX,
  INIT_DEPOSIT_MIN,
  INIT_DEPOSIT_STEP,
  MONTHLY_RATE_DEFAULT,
  MONTHLY_RATE_DEFAULT_SK,
  MONTHLY_RATE_MAX,
  MONTHLY_RATE_MIN,
  MONTHLY_RATE_STEP,
} from "./config";
/* --------------------- Import types --------------------- */
// eslint-disable-next-line no-duplicate-imports
import {
  Application,
  ComplianceWarning,
  DepositData,
  Environment,
  FixedLengthArray,
  Fund,
  GraphicDataPerformance,
  JSONFactsheetAPI,
  Locale,
  PerformanceData,
  ShortWarning,
  TableData,
} from "./types";
/* --------------------- Import helper functions --------------------- */
import {
  calculateDepositChart,
  calculatePerformance,
  fetchFactsheetWarnings,
  fetchPerformance,
  getDateFnsLocaleForConfig,
  getMuixLocaleForAppLocale,
  getPerformanceData,
  useEvent,
  isNotSlovakiaDomain, getCurrencyForInvestmentCompany,
} from "./utils";

/* --------------------- Import localization --------------------- */
import localization from "./localization";

/* --------------------- Import MUI components --------------------- */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  Grid,
  Grow,
  Stack,
  Typography,
} from "@mui/material";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { DateTime } from "luxon";

/* --------------------- Import custom UI ./components --------------------- */
import AutocompleteComponent from "./components/AutocompleteComponent";
import SliderComponent from "./components/SliderComponent";
import DatePickerComponent from "./components/DatePickerComponent";
import IndexedRateComponent from "./components/IndexedRateComponent";
import DepositComponent from "./components/DepositComponent";
import MonthlyRateComponent from "./components/MonthlyRateComponent";
import ChartComponent from "./components/ChartComponent";
import TableComponent from "./components/TableComponent";
import ComplianceComponent from "./components/ComplianceComponent";
import type { ChartData, ChartDataset } from "chart.js";

type AppProps = {
  locale: Locale;
  environment: Environment;
  application: Application;
};

type PerformanceCache = {
  key: string;
  graphicDataPerformance: GraphicDataPerformance[];
};

/* --------------------- React App entrypoint --------------------- */

function setDefaultMonthlyRateValue(locale: Locale) {
  if (locale === Locale.skSK || locale === Locale.enSK) {
    return MONTHLY_RATE_DEFAULT_SK.toString();
  }
  return MONTHLY_RATE_DEFAULT.toString();
}

function setDefaultDepositValue(locale: Locale) {
  if (locale === Locale.skSK || locale === Locale.enSK) {
    return DEPOSIT_DEFAULT_SK.toString();
  }
  return DEPOSIT_DEFAULT.toString();
}

function getDefaultDurationByLocale(locale: Locale) {
  if (locale === Locale.skSK || locale === Locale.enSK) {
    return 5;
  }
  return 10;
}

const App = (props: AppProps) => {
  /* --------------------- Localization --------------------- */
  const { locale, environment, application } = props;
  const {
    localeString,
    timezone,
    titlePerformanceCalculator,
    titleHistoric,
    dropdownChooseMainFond,
    dropdownChooseAdditionalFonds,
    checkboxAga,
    labelIndexed,
    labelInitial,
    labelMonthly,
    labelDatepicker,
    chartTitlePerformance,
    chartTitleInvestition,
    warningAdjusted,
    warningNotAvailableLifetime,
    infoMaximumFonds,
    infoRuntime,
    disclaimerIndexed,
    disclaimerCalculation,
    disclaimerOnlineOrder,
    dropdownPlaceholder,
    toString,
    resetButtonLabel,
  } = localization[locale];

  /* --------------------- Initialize timeframe duration --------------------- */

  const END_DATE_DEFAULT: DateTime = DateTime.now().setZone(timezone).startOf("day");
  const START_DATE_DEFAULT: DateTime = END_DATE_DEFAULT.minus({
    year: getDefaultDurationByLocale(locale),
  });

  /* --------------------- Initialize React state variables --------------------- */

  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const [isFondInputDisabled, setIsFondInputDisabled] = useState<boolean>(false);
  const [shortWarning, setShortWarning] = useState<ShortWarning[]>([]);
  const [fromDateWarning, setFromDateWarning] = useState<React.ReactNode>(null);
  const [toDateWarning, setToDateWarning] = useState<React.ReactNode>(null);
  const [isShorterSeriesWarning, setIsShorterSeriesWarning] = useState<boolean>(false);
  const [isMaxFondsSelected, setIsMaxFondsSelected] = useState<boolean>(false);
  const [complianceWarnings, setComplianceWarnings] = useState<ComplianceWarning[]>([]);

  const [datePickerOpen1, setDatePickerOpen1] = useState<boolean>(false);
  const [datePickerOpen2, setDatePickerOpen2] = useState<boolean>(false);
  const [depositSliderValue, setDepositSliderValue] = useState<number>(parseInt(setDefaultDepositValue(locale)));
  const [sliderDragValue2, setSliderDragValue2] = useState<number>(parseInt(setDefaultMonthlyRateValue(locale)));
  const [sliderTooltipFund, setSliderTooltipFund] = useState<Fund>(null);

  const [fundlist, setFundlist] = useState<Fund[]>([]);

  const [mainFund, setMainFund] = useState<Fund>(null);
  const [compareFunds, setCompareFunds] = useState<Fund[]>([]);
  const [fromToDate, setFromToDate] = useState<FixedLengthArray<[DateTime, DateTime]>>([
    START_DATE_DEFAULT,
    END_DATE_DEFAULT,
  ]);
  const [indexing, setIndexing] = useState<number>(INDEXED_RATE_DEFAULT);
  const [isFeeEnabled, setIsFeeEnabled] = useState<boolean>(false);
  const [initDeposit, setInitDeposit] = useState<number>(parseInt(setDefaultDepositValue(locale)));
  const [monthlyRate, setMonthlyRate] = useState<number>(
    parseInt(setDefaultMonthlyRateValue(locale)),
  );

  const [performanceCache, setPerformanceCache] = useState<PerformanceCache[]>([]);
  const [chartData, setChartData] = useState<
    ChartData<"line"> & {
      labels: string[];
    }
  >({
    labels: [],
    datasets: [],
  });

  const [tableData, setTableData] = useState<TableData[]>([]);

  const hideAdditionalFundAutoCompleteComponentForLocales = [Locale.roRO, Locale.enRO];
  const hideUpfrontFeeCheckboxForLocales = [Locale.hrHR, Locale.enHR];
  const currency = getCurrencyForInvestmentCompany({
    investmentCompanyShort: mainFund?.investmentCompanyShort,
    locale: localization[locale],
  });
  function setWarningIfDomainNotSlovakia(label: string, startDate: DateTime, endDate: DateTime) {
    if (isNotSlovakiaDomain(locale)) {
      setFromDateWarning(
        warningAdjusted(label, startDate.toLocaleString(), endDate.toLocaleString()),
      );
    }
  }

  /* --------------------- Initialize React reference variables --------------------- */

  const prevInput = useRef({
    mainFund,
    compareFunds,
    fromToDate,
    indexing,
    isFeeEnabled,
    initDeposit,
    monthlyRate,
  }).current;

  const textFieldRef1 = useRef<React.MutableRefObject<HTMLInputElement | null>>(null);
  const textFieldRef2 = useRef<React.MutableRefObject<HTMLInputElement | null>>(null);

  /* --------------------- Iframe auto height / width fix --------------------- */

  const resizeIframe = () => {
    const message = {
      height: document.body.scrollHeight,
      width: document.body.scrollWidth,
    };
    window.top?.postMessage(message, "*");
  };

  useEvent("load", resizeIframe);
  useEvent("resize", resizeIframe);

  useEffect(() => {
    resizeIframe();
  }, [complianceWarnings]);

  /* --------------------- Initialize empty deposit chart on first DOM load --------------------- */

  useEffect(() => {
    const chartData = calculateDepositChart(
      locale,
      START_DATE_DEFAULT,
      END_DATE_DEFAULT,
      parseInt(setDefaultDepositValue(locale)),
      parseInt(setDefaultMonthlyRateValue(locale)),
      INDEXED_RATE_DEFAULT,
    );
    setChartData(chartData);
  }, []);

  useEffect(() => {
    if (mainFund !== null) setSliderTooltipFund(mainFund);
  }, [mainFund]);
  /* --------------------- Performance calculator logic --------------------- */

  useEffect(() => {
    /* --------------------- Build performance Chart.js dataset --------------------- */

    const buildPerformanceDataset = async (
      fund: Fund,
      fromDate: DateTime,
      toDate: DateTime,
      isFeeEnabled: boolean,
      depositData: DepositData,
      color: string,
      isHauptfond: boolean,
    ): Promise<{
      chartDataset: ChartDataset<"line">;
      totalPerformance: number | null;
      hidden: boolean;
      shortWarningMessage: React.ReactNode | null;
    }> => {
      // Initialize return values
      let chartDataset: ChartDataset<"line"> = { data: [] };
      let totalPerformance: number | null = null;
      let hidden = false;
      let shortWarningMessage: React.ReactNode | null = null;
      // Fix "fund could be undefined" warning
      if (fund === null) return { chartDataset, totalPerformance, hidden, shortWarningMessage };
      const { masterISIN, label } = fund;

      // If main fund was changed, reset to default timeframe duration and exit function
      if (prevInput.mainFund !== mainFund && prevInput.mainFund !== null) {
        setFromToDate([START_DATE_DEFAULT, END_DATE_DEFAULT]);
        return { chartDataset, totalPerformance, hidden, shortWarningMessage };
      }

      /* --------------------- Fetch performance data from API --------------------- */

      let graphicDataPerformance: GraphicDataPerformance[];
      /* beforePerformanceJson is used in case first Date isn't available to set warning message correctly */
      let previousGraphicDataPerformance: GraphicDataPerformance[];
      // Check if performance data is already in cache, else fetch from API and save to cache
      const key = `${masterISIN}.${fromDate.toMillis()}.${toDate.toMillis()}`;
      const cachedResponse = performanceCache.find((_) => _.key === key);
      if (cachedResponse) {
        graphicDataPerformance = cachedResponse.graphicDataPerformance;
      } else {
        graphicDataPerformance = await fetchPerformance(
          locale,
          environment,
          masterISIN,
          fromDate,
          toDate,
          application,
        );
        setPerformanceCache((prev) => [...prev, { key, graphicDataPerformance }]);
      }

      let performanceData: PerformanceData[];
      try {
        performanceData = getPerformanceData(graphicDataPerformance);
      } catch (e) {
        performanceData = [];
      }

      const beforeKey = `${key}.before`;
      const beforeCachedResponse = performanceCache.find((_) => _.key === beforeKey);
      if (beforeCachedResponse) {
        previousGraphicDataPerformance = beforeCachedResponse.graphicDataPerformance;
      } else {
        previousGraphicDataPerformance = await fetchPerformance(
          locale,
          environment,
          masterISIN,
          fromDate.minus({ days: 7 }),
          fromDate,
          application,
        );
        setPerformanceCache((prev) => [
          ...prev,
          { key: beforeKey, graphicDataPerformance: previousGraphicDataPerformance },
        ]);
      }

      let beforePerformanceData: PerformanceData[];
      try {
        beforePerformanceData = getPerformanceData(previousGraphicDataPerformance);
      } catch (e) {
        beforePerformanceData = [];
      }

      /* --------------------- Fund timeframe duration checks --------------------- */

      // Check whether timeframe start and/or end date need to be adjusted
      // If adjusted => exit function => "setFromToDate" state update will trigger this function again with adjusted dates
      const firstSeriesDate: DateTime = DateTime.fromMillis(performanceData[0].timestamp)
        .setZone(timezone)
        .startOf("day");
      const lastSeriesDate: DateTime = DateTime.fromMillis(performanceData.slice(-1)[0].timestamp)
        .setZone(timezone)
        .startOf("day");

      let startDate: DateTime | null = null;
      let endDate: DateTime | null = null;

      // Adjust start date in case main fund's performance data is shorter by at least 1 day from the start date of the timeframe (selected by user or default)
      if (isHauptfond && firstSeriesDate > fromDate) {
        if (beforePerformanceData.length === 0) {
          startDate = firstSeriesDate;
        } else {
          startDate = DateTime.fromMillis(beforePerformanceData.slice(-1)[0].timestamp)
            .setZone(timezone)
            .startOf("day");
        }
      }

      // Adjust end date in case main fund's performance data is shorter by at least 1 day from the end date of the timeframe (selected by user or default)
      if (isHauptfond && lastSeriesDate < toDate) {
        endDate = lastSeriesDate;
      }

      if (startDate !== null && endDate !== null) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setFromToDate([startDate!, endDate!]);
        setWarningIfDomainNotSlovakia(label, startDate, endDate);
        return { chartDataset, totalPerformance, hidden, shortWarningMessage };
      } else if (startDate === null && endDate !== null) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setFromToDate((prevState) => [prevState[0], endDate!]);
        setWarningIfDomainNotSlovakia(label, firstSeriesDate, endDate);
        return { chartDataset, totalPerformance, hidden, shortWarningMessage };
      } else if (startDate !== null && endDate === null) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setFromToDate((prevState) => [startDate!, prevState[1]]);
        setWarningIfDomainNotSlovakia(label, startDate, lastSeriesDate);
        return { chartDataset, totalPerformance, hidden, shortWarningMessage };
      }

      // Check whether main fund's timeframe is shorter than 10 years and display a warning (per compliance)
      const seriesDuration = lastSeriesDate.diff(firstSeriesDate, "years").toObject().years ?? 0;
      if (locale === Locale.skSK || locale === Locale.enSK) {
        // Changed the variable from 5 to 4.95 because the last fond entry of the CEE countries are not always up-to-date
        if (isHauptfond && seriesDuration < 4.95) {
          setIsShorterSeriesWarning(true);
        } else if (isHauptfond && seriesDuration >= 4.95) {
          setIsShorterSeriesWarning(false);
        }
      } else {
        if (isHauptfond && seriesDuration < 10) {
          setIsShorterSeriesWarning(true);
        } else if (isHauptfond && seriesDuration >= 10) {
          setIsShorterSeriesWarning(false);
        }
      }

      // Check whether comparison fund's timeframe is shorter than main fund's timeframe (hide and display a notice per compliance)
      if (
        (!isHauptfond && firstSeriesDate > fromDate) ||
        (!isHauptfond && lastSeriesDate < toDate && isNotSlovakiaDomain(locale))
      ) {
        hidden = true;
        shortWarningMessage = warningNotAvailableLifetime(
          label,
          firstSeriesDate.toLocaleString(),
          lastSeriesDate.toLocaleString(),
        );
      }

      /* --------------------- Finally, apply performance calculation and transform the result into a Chart.js dataset --------------------- */

      const fee = isFeeEnabled ? performanceData[0].fee / 100.0 : 0;
      const data = calculatePerformance(performanceData, fee, depositData, locale);

      chartDataset = {
        data,
        hidden,
        label,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 20,
        borderWidth: 2,
        spanGaps: true,
        borderColor: color,
        backgroundColor: color,
      };

      const totalInvestition = Number(depositData.deposit.at(-1));
      const finalAmount = Number(chartDataset.data.filter((__) => __ !== null).slice(-1)[0]);

      totalPerformance = finalAmount / totalInvestition - 1.0;

      return {
        chartDataset,
        totalPerformance,
        hidden,
        shortWarningMessage,
      };
    };

    /* --------------------- Update logic for main fund changes --------------------- */

    const mainFundUpdated = async () => {
      // Fix "fund could be undefined" warning
      if (mainFund === null) return;
      setFromDateWarning(null);
      setToDateWarning(null);
      setTableData((_) => [
        {
          masterISIN: mainFund.masterISIN,
          label: mainFund.label,
          investmentCompanyShort: mainFund!.investmentCompanyShort,
          finalAmount: null,
          totalPerformance: null,
        },
        ..._.slice(1),
      ]);
      setComplianceWarnings((_) => [
        {
          masterISIN: mainFund.masterISIN,
          warning: [],
          title: null,
          esmaTextOnly: null,
        },
        ..._.slice(1),
      ]);
      const depositData: DepositData = {
        deposit: chartData.datasets[0].data,
        dates: chartData.labels.map((date: string) => DateTime.fromISO(date).setZone(timezone)),
      };
      const color = CHART_COLOR_SET[0];
      const { chartDataset, totalPerformance, hidden } = await buildPerformanceDataset(
        mainFund,
        fromToDate[0],
        fromToDate[1],
        isFeeEnabled,
        depositData,
        color,
        true,
      );
      if (chartDataset.data.length === 0 && totalPerformance === null && hidden === false) return;
      setChartData((_) => ({
        ..._,
        datasets: [_.datasets[0], chartDataset, ..._.datasets.slice(2)],
      }));
      setTableData((_) => [
        {
          masterISIN: mainFund.masterISIN,
          label: chartDataset.label!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          investmentCompanyShort: mainFund!.investmentCompanyShort,
          finalAmount: Number(chartDataset.data.filter((__) => __ !== null).slice(-1)[0]),
          totalPerformance,
        },
        ..._.slice(1),
      ]);
      const factsheetWarning = await fetchFactsheetWarnings(
        locale,
        environment,
        mainFund.masterISIN,
        application,
      );
      setComplianceWarnings((_) => [
        {
          masterISIN: mainFund.masterISIN,
          warning: factsheetWarning?.WARNINGS[0]?.WARNINGS,
          title: factsheetWarning?.WARNINGS_META[0]?.TITLE_MESSAGE,
          esmaTextOnly: factsheetWarning?.ESMA_TEXT_ONLY[0].ESMA_TEXT_ONLY,
        },
        ..._.slice(1),
      ]);
    };

    /* --------------------- Update logic for comparison fund changes --------------------- */

    const compareFundsUpdated = async () => {
      if (compareFunds.length === 4) {
        setIsMaxFondsSelected(true);
      } else {
        setIsMaxFondsSelected(false);
      }
      if (prevInput.compareFunds.length < compareFunds.length) {
        // New compare fund was added
        const newFund = compareFunds.filter((_) => !prevInput.compareFunds.includes(_))[0]!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
        setTableData((_) => [
          ..._,
          {
            masterISIN: newFund.masterISIN,
            label: newFund.label,
            investmentCompanyShort: newFund!.investmentCompanyShort,
            finalAmount: null,
            totalPerformance: null,
          },
        ]);
        setComplianceWarnings((_) => [
          ..._,
          {
            masterISIN: newFund.masterISIN,
            warning: [],
            title: null,
            esmaTextOnly: null,
          },
        ]);
        const depositData: DepositData = {
          deposit: chartData.datasets[0].data,
          dates: chartData.labels.map((date: string) => DateTime.fromISO(date).setZone(timezone)),
        };
        const color = CHART_COLOR_SET[compareFunds.length % CHART_COLOR_SET.length];
        const { chartDataset, totalPerformance, hidden, shortWarningMessage } =
          await buildPerformanceDataset(
            newFund,
            fromToDate[0],
            fromToDate[1],
            isFeeEnabled,
            depositData,
            color,
            false,
          );
        setChartData((_) => ({
          ..._,
          datasets: [..._.datasets, chartDataset],
        }));
        setShortWarning((_) => [
          ..._,
          {
            label: newFund.label,
            isHidden: hidden,
            message: shortWarningMessage,
          },
        ]);
        setTableData((_) =>
          _.map((row) =>
            row.label === newFund.label
              ? {
                  masterISIN: newFund.masterISIN,
                  label: chartDataset.label!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
                  investmentCompanyShort: newFund!.investmentCompanyShort,
                  finalAmount: Number(chartDataset.data.filter((__) => __ !== null).slice(-1)[0]),
                  totalPerformance,
                }
              : row,
          ),
        );
        const warning = await fetchFactsheetWarnings(
          locale,
          environment,
          newFund.masterISIN,
          application,
        );
        setComplianceWarnings((_) =>
          _.map((row) =>
            row.masterISIN === newFund.masterISIN
              ? {
                  masterISIN: newFund.masterISIN,
                  warning: warning.WARNINGS[0]?.WARNINGS,
                  title: warning.WARNINGS_META[0].TITLE_MESSAGE,
                  esmaTextOnly: warning.ESMA_TEXT_ONLY[0].ESMA_TEXT_ONLY,
                }
              : row,
          ),
        );
      } else {
        // Compare fund was removed
        const removedFund = prevInput.compareFunds.filter((_) => !compareFunds.includes(_))[0]!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
        setChartData((_) => ({
          ..._,
          datasets: [..._.datasets.filter((_dataset) => _dataset.label !== removedFund.label)],
        }));
        setShortWarning((_) => _.filter((row) => row.label !== removedFund.label));
        setTableData((_) => _.filter((row) => row.label !== removedFund.label));
        setComplianceWarnings((_) => _.filter((row) => row.masterISIN !== removedFund.masterISIN));
      }
    };

    /* --------------------- Update logic when everything needs to be updated --------------------- */

    const updateAll = async () => {
      setIsFondInputDisabled(true);
      const depositChart = calculateDepositChart(
        locale,
        fromToDate[0],
        fromToDate[1],
        initDeposit,
        monthlyRate,
        indexing,
      );
      if (!mainFund) {
        setChartData(depositChart);
        setIsFondInputDisabled(false);
        return;
      }
      const allFunds = [mainFund, ...compareFunds];
      setTableData(
        allFunds.map((fund) => ({
          masterISIN: fund!.masterISIN, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          label: fund!.label, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          finalAmount: null,
          totalPerformance: null,
          investmentCompanyShort: fund!.investmentCompanyShort,
        })),
      );
      setComplianceWarnings(
        allFunds.map((fund) => ({
          masterISIN: fund!.masterISIN, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          warning: [],
          title: null,
          esmaTextOnly: null,
        })),
      );
      const depositData: DepositData = {
        deposit: depositChart.datasets[0].data,
        dates: depositChart.labels.map((date: string) => DateTime.fromISO(date).setZone(timezone)),
      };
      const totalPerformances: (number | null)[] = [];
      const chartDatasets: ChartDataset<"line">[] = [];
      const isDatasetHidden: boolean[] = [];
      const warningMessages: React.ReactNode[] = [];
      const complianceMessages: JSONFactsheetAPI[] = [];
      for (const [i, fund] of allFunds.entries()) {
        const color = CHART_COLOR_SET[i % CHART_COLOR_SET.length];
        const { chartDataset, totalPerformance, hidden, shortWarningMessage } =
          await buildPerformanceDataset(
            fund,
            fromToDate[0],
            fromToDate[1],
            isFeeEnabled,
            depositData,
            color,
            i === 0,
          );
        if (i === 0 && chartDataset === null && performance === null && !hidden) return;
        const complianceMessage = await fetchFactsheetWarnings(
          locale,
          environment,
          fund!.masterISIN, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          application,
        );
        totalPerformances.push(totalPerformance);
        chartDatasets.push(chartDataset);
        isDatasetHidden.push(hidden);
        warningMessages.push(shortWarningMessage);
        complianceMessages.push(complianceMessage);
      }
      setChartData((_) => ({
        ..._,
        labels: depositChart.labels,
        datasets: [depositChart.datasets[0], ...chartDatasets],
      }));
      setShortWarning(
        chartDatasets.map((row, i) => ({
          label: row.label!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          isHidden: isDatasetHidden[i],
          message: warningMessages[i],
        })),
      );
      setTableData(
        chartDatasets.map((row, i) => ({
          masterISIN: allFunds[i]!.masterISIN, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          label: row.label!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          investmentCompanyShort: allFunds[i]!.investmentCompanyShort,
          finalAmount: Number(row.data.filter((_) => _ !== null).slice(-1)[0]),
          totalPerformance: totalPerformances[i],
        })),
      );
      setComplianceWarnings(
        allFunds.map((row, i) => ({
          masterISIN: row!.masterISIN, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          warning: complianceMessages[i].WARNINGS[0]?.WARNINGS,
          title: complianceMessages[i].WARNINGS_META[0].TITLE_MESSAGE,
          esmaTextOnly: complianceMessages[i].ESMA_TEXT_ONLY[0].ESMA_TEXT_ONLY,
        })),
      );
      setIsFondInputDisabled(false);
    };

    // Depending on what variables changed execute an update
    if (prevInput.mainFund !== mainFund) {
      mainFundUpdated();
      if (isFirstRender) {
        /*
                    Spaghetti Code fix
                    The first time it might not update the TableData correctly, but I don't understand why that happens

                    "might not update", as it works correctly if the dates were adjusted automatically or an updateAll happened.
                 */
        setIsFirstRender(false);
        updateAll();
      }
    }
    if (prevInput.compareFunds !== compareFunds) {
      compareFundsUpdated();
    }
    if (
      prevInput.fromToDate !== fromToDate ||
      prevInput.indexing !== indexing ||
      prevInput.isFeeEnabled !== isFeeEnabled ||
      prevInput.initDeposit !== initDeposit ||
      prevInput.monthlyRate !== monthlyRate
    ) {
      updateAll();
    }

    // Resize iframe
    resizeIframe();

    // Override previously saved input values with new ones
    return () => {
      prevInput.mainFund = mainFund;
      prevInput.compareFunds = compareFunds;
      prevInput.fromToDate = fromToDate;
      prevInput.indexing = indexing;
      prevInput.isFeeEnabled = isFeeEnabled;
      prevInput.initDeposit = initDeposit;
      prevInput.monthlyRate = monthlyRate;
    };
  }, [mainFund, compareFunds, fromToDate, indexing, isFeeEnabled, initDeposit, monthlyRate]);

  /* --------------------- UI --------------------- */
  const upfrontFeeCheckboxHidden = hideUpfrontFeeCheckboxForLocales.toLocaleString().includes(locale.toString()).valueOf();
  return (
    <Box>
      {/* --------------------- Header --------------------- */}
      <Container maxWidth="lg">
        <Stack paddingTop={4} paddingBottom={4} spacing={2}>
          <Typography variant="h2" fontSize={32} fontWeight={700} color={COLOR.grey6}>
            {titlePerformanceCalculator}
          </Typography>
          <Typography fontWeight="bold" variant="body2" fontSize={14} color={COLOR.grey2}>
            {titleHistoric}
          </Typography>
        </Stack>
      </Container>
      <Box width="full" borderBottom={`1px solid ${COLOR.grey5}`}></Box>
      {/* --------------------- Controls --------------------- */}
      <Box
        width="full"
        bgcolor={COLOR.grey1}
        paddingBottom={2}
        paddingTop={2}
        justifyContent="space-around"
      >
        <Container maxWidth="lg">
          <Grid container spacing={0}>
            <Grid item md={5} xs={12} order={{ md: 1, xs: 1 }} padding={1} alignSelf="flex-end">
              <AutocompleteComponent
                setFundlist={useCallback((fundlist: Fund[]) => setFundlist(fundlist), [])}
                disabled={false}
                options={fundlist}
                label={dropdownChooseMainFond}
                dropdownPlaceholder={dropdownPlaceholder}
                multiple={false}
                disableClearable={true}
                setValue={useCallback((fund: unknown) => setMainFund(fund as Fund), [])}
                disableSelect={false}
                selectedOptions={useMemo(
                  () => [mainFund, ...compareFunds],
                  [mainFund, compareFunds],
                )}
                locale={locale}
                environment={environment}
                application={application}
              />
            </Grid>
            <Grid item md={4} xs={8} padding={1} order={{ md: 2, xs: 3 }} alignSelf="flex-end">
              <Box paddingBottom={2}>
                <Typography variant="caption" color={"rgba(0, 0, 0, 0.6)"}>
                  {labelDatepicker}
                </Typography>
              </Box>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={getDateFnsLocaleForConfig(locale)}
                localeText={
                  (getMuixLocaleForAppLocale(locale) as any).components?.MuiLocalizationProvider?.defaultProps?.localeText
                  ?? {}
                }
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <DatePickerComponent
                    open={datePickerOpen1}
                    value={fromToDate[0]}
                    onOpen={useCallback(() => setDatePickerOpen1(true), [])}
                    onClose={useCallback(() => setDatePickerOpen1(false), [])}
                    onAccept={useCallback((date: Date | null) => {
                      setFromDateWarning(null);
                      setToDateWarning(null);
                      if (date === null) return;
                      setFromToDate((_) => [
                        DateTime.fromJSDate(date).setZone(timezone).startOf("day"),
                        _[1],
                      ]);
                    }, [])}
                    onClick={useCallback(() => setDatePickerOpen1(true), [])}
                  />
                  <span>{toString}</span>
                  <DatePickerComponent
                    open={datePickerOpen2}
                    value={fromToDate[1]}
                    onOpen={useCallback(() => setDatePickerOpen2(true), [])}
                    onClose={useCallback(() => setDatePickerOpen2(false), [])}
                    onAccept={useCallback((date: Date | null) => {
                      setFromDateWarning(null);
                      setToDateWarning(null);
                      if (date === null) return;
                      setFromToDate((_) => [
                        _[0],
                        DateTime.fromJSDate(date).setZone(timezone).startOf("day"),
                      ]);
                    }, [])}
                    onClick={useCallback(() => setDatePickerOpen2(true), [])}
                  />
                </Stack>
              </LocalizationProvider>
            </Grid>
            <Grid item md={3} xs={4} padding={1} order={{ md: 3, xs: 4 }} >
              <FormGroup sx={{ paddingBottom: upfrontFeeCheckboxHidden ? 5 : 1}}>
                <FormControlLabel
                  style={{ display: upfrontFeeCheckboxHidden ? "none" : "block"}}
                  control={
                    <Checkbox
                      onChange={(e) => {
                        setIsFeeEnabled(e.target.checked);
                      }}
                    />
                  }
                  label={<Typography variant="caption">{checkboxAga}</Typography>}
                />
              </FormGroup>
              <IndexedRateComponent
                label={labelIndexed}
                defaultValue={INDEXED_RATE_DEFAULT.toString()}
                onBlur={useCallback((e) => {
                  if (!e.target.value || parseInt(e.target.value) < 0) {
                    setIndexing(0);
                  } else {
                    setIndexing(parseInt(e.target.value));
                  }
                }, [])}
                onKeyDown={useCallback((e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    if (!target.value || parseInt(target.value) < 0) {
                      setIndexing(0);
                    } else {
                      setIndexing(parseInt(target.value));
                    }
                  }
                }, [])}
              />
            </Grid>
            <Grid item md={5} xs={12} order={{ md: 4, xs: 2 }} padding={1} style={{ display: hideAdditionalFundAutoCompleteComponentForLocales.toLocaleString().includes(locale.toString()).valueOf() ? "none" : "block" }}>
              <AutocompleteComponent
                setFundlist={useCallback((_: Fund[]) => setFundlist(_), [])}
                disabled={useMemo(() => {
                  return isFondInputDisabled || !(chartData.datasets.length > 1);
                }, [isFondInputDisabled, chartData])}
                options={fundlist}
                label={dropdownChooseAdditionalFonds}
                dropdownPlaceholder={dropdownPlaceholder}
                multiple={true}
                disableClearable={true}
                setValue={useCallback((funds: unknown) => setCompareFunds(funds as Fund[]), [])}
                disableSelect={useMemo(() => {
                  return compareFunds.length >= 4;
                }, [compareFunds])}
                selectedOptions={useMemo(
                  () => [mainFund, ...compareFunds],
                  [mainFund, compareFunds],
                )}
                locale={locale}
                environment={environment}
                application={application}
              />
            </Grid>
            <Grid item md={4} xs={6} padding={1} order={{ md: 5, xs: 5 }}>
              <DepositComponent
                locale={locale}
                fund={mainFund}
                inputRef={textFieldRef1}
                label={labelInitial}
                defaultValue={setDefaultDepositValue(locale)}
                onBlur={useCallback((e) => {
                  if (!e.target.value || parseInt(e.target.value) < 0) {
                    setInitDeposit(0);
                    setDepositSliderValue(0);
                  } else {
                    setInitDeposit(parseInt(e.target.value));
                    setDepositSliderValue(parseInt(e.target.value));
                  }
                }, [])}
                onKeyDown={useCallback((e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    if (!target.value || parseInt(target.value) < 0) {
                      setInitDeposit(0);
                      setDepositSliderValue(0);
                    } else {
                      setInitDeposit(parseInt(target.value));
                      setDepositSliderValue(parseInt(target.value));
                    }
                  }
                }, [])}
              />
              <SliderComponent
                locale={locale}
                fund={sliderTooltipFund}
                value={depositSliderValue}
                handleChange={useCallback((e, val) => {
                  setDepositSliderValue(Number(val));
                }, [])}
                label={labelInitial}
                step={INIT_DEPOSIT_STEP}
                min={INIT_DEPOSIT_MIN}
                max={INIT_DEPOSIT_MAX}
                handleChangeCommited={useCallback((e, val) => {
                  if (textFieldRef1.current === null) return;
                  setInitDeposit(Number(val));
                  const target = textFieldRef1.current as unknown as HTMLInputElement;
                  target.value = val.toString();
                }, [])}
              />
            </Grid>
            <Grid item md={3} xs={6} padding={1} order={{ md: 6, xs: 6 }}>
              <MonthlyRateComponent
                locale={locale}
                fund={mainFund}
                inputRef={textFieldRef2}
                label={labelMonthly}
                defaultValue={setDefaultMonthlyRateValue(locale)}
                onBlur={useCallback((e) => {
                  if (!e.target.value || parseInt(e.target.value) < 0) {
                    setMonthlyRate(0);
                    setSliderDragValue2(0);
                  } else {
                    setMonthlyRate(parseInt(e.target.value));
                    setSliderDragValue2(parseInt(e.target.value));
                  }
                }, [])}
                onKeyDown={useCallback((e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    if (!target.value || parseInt(target.value) < 0) {
                      setMonthlyRate(0);
                      setSliderDragValue2(0);
                    } else {
                      setMonthlyRate(parseInt(target.value));
                      setSliderDragValue2(parseInt(target.value));
                    }
                  }
                }, [])}
              />
              <SliderComponent
                locale={locale}
                fund={sliderTooltipFund}
                value={sliderDragValue2}
                handleChange={useCallback((e, val) => {
                  setSliderDragValue2(Number(val));
                }, [])}
                label={labelMonthly}
                step={MONTHLY_RATE_STEP}
                min={MONTHLY_RATE_MIN}
                max={MONTHLY_RATE_MAX}
                handleChangeCommited={useCallback((e, val) => {
                  if (textFieldRef2.current === null) return;
                  setMonthlyRate(Number(val));
                  const target = textFieldRef2.current as unknown as HTMLInputElement;
                  target.value = val.toString();
                }, [])}
              />
              <Button
                size="small"
                sx={{ float: "right" }}
                onClick={useCallback(() => window.location.reload(), [])}
              >
                {resetButtonLabel}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* --------------------- Warnings --------------------- */}
      <Container maxWidth="lg" sx={{ paddingTop: 2, paddingBottom: 2 }} role="status">
        {isMaxFondsSelected && (
          <Grow in={isMaxFondsSelected !== null}>
            <Alert severity="info" sx={{ margin: 1, color: "#303030" }}>
              {infoMaximumFonds}
            </Alert>
          </Grow>
        )}
        {fromDateWarning !== null && (
          <Grow in={fromDateWarning !== null}>
            <Alert severity="warning" sx={{ margin: 1 }}>
              {fromDateWarning}
            </Alert>
          </Grow>
        )}
        {toDateWarning !== null && (
          <Grow in={toDateWarning !== null}>
            <Alert severity="warning" sx={{ margin: 1 }}>
              {toDateWarning}
            </Alert>
          </Grow>
        )}
        {shortWarning.map(({ isHidden, message, label }) => {
          if (isHidden) {
            return (
              <Grow in={true} key={label}>
                <Alert severity="warning" sx={{ margin: 1 }}>
                  {message}
                </Alert>
              </Grow>
            );
          } else {
            return null;
          }
        })}
        {isShorterSeriesWarning && (
          <Grow in={isShorterSeriesWarning !== null}>
            <Alert severity="warning" sx={{ margin: 1 }}>
              {infoRuntime}
            </Alert>
          </Grow>
        )}
      </Container>
      <Container maxWidth="lg" sx={{ paddingTop: 3, paddingBottom: 2 }}>
        {/* --------------------- Total deposit --------------------- */}
        <Stack
          justifyContent="space-between"
          direction="row"
          spacing={2}
          paddingBottom={2}
          paddingLeft={4}
        >
          <Typography variant="body2" fontWeight="bold">
            {chartTitlePerformance}
          </Typography>
          <Typography variant="body2" aria-live={"polite"} fontWeight="bold">
            {chartData.datasets[0] && (
              <>
                {chartTitleInvestition}{" "}
                {new Intl.NumberFormat(localeString, {
                  style: "currency",
                  currency: currency,
                }).format(Number(chartData.datasets[0].data?.at(-1)))}
              </>
            )}
          </Typography>
        </Stack>
        {/* --------------------- Chart --------------------- */}
        <ChartComponent data={chartData} fund={mainFund} locale={locale} />
      </Container>
      <Box width="full" bgcolor={COLOR.grey1}>
        {/* --------------------- Table --------------------- */}
        <Container maxWidth="lg">
          <TableComponent
            tableData={tableData}
            shortWarning={shortWarning}
            locale={locale}
            isFeeEnabled={isFeeEnabled}
          />
        </Container>
        {/* --------------------- Disclaimer Indexed --------------------- */}
        <Container maxWidth="lg" sx={{ paddingTop: 3, paddingBottom: 0 }}>
          <Typography variant="caption" lineHeight="normal">
            {disclaimerIndexed}
          </Typography>
        </Container>
        {/* --------------------- Disclaimer Calculation --------------------- */}
        <Container maxWidth="lg" sx={{ paddingTop: 3, paddingBottom: 0 }}>
          <Typography variant="caption" lineHeight="normal">
            {disclaimerCalculation}
          </Typography>
        </Container>
        {/* --------------------- Disclaimer Online Order --------------------- */}
        <Container maxWidth="lg" sx={{ paddingTop: 3, paddingBottom: 3 }}>
          <Typography variant="caption" lineHeight="normal">
            {disclaimerOnlineOrder}
          </Typography>
        </Container>
      </Box>
      {/* --------------------- Compliance messages --------------------- */}
      <ComplianceComponent title={complianceWarnings} warnings={complianceWarnings} />
    </Box>
  );
};

export default App;
